/*
TODO: move code from following files to one generic file and use that 
    src/components/modals/UserDetails/components/DetailsForm/index.tsx
    src/components/eventPage/components/RebookingForm/DetailsForm.tsx
    src/components/pay/DetailsForm/index.tsx
*/
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import type { IEvent, IHost, IStore } from "@Interfaces";
import { classNames } from "repoV2/utils/common/render/classNames";
import {
    createAction,
    isIndia,
    ACTION_TYPES,
    INDIA,
    SELECTORS,
    isEmpty,
} from "@Utils";
import {
    COUNTRY_CODE_DROPDOWN_OPTIONS as countryCodes,
    getCountryCodeDropdownOptionFromKey,
} from "utils/location";
import type { IDropdownSelector } from "@Modules/common/Form/FormInputs/DropdownSelector/IDropdownSelector";
import { DropdownSelector } from "@Modules/common/Form";
import { indian_state_gstin_to_state } from "repoV2/features/Common/modules/Location/constants/indianStatesGstData";
import { gst_details_gstin_note } from "repoV2/features/CheckoutPages/modules/PaymentDetailsSection/modules/FormGroups/modules/GSTDetailsFormGroup/GSTDetailsFormGroup.constants";
import { checkIsNeedToPrefillFormData } from "repoV2/features/Common/modules/FormData/utils/checkIsNeedToPrefillFormData";
import type { IUserDetails } from "../../IUserDetails";
import type { IDetailsForm } from "./IDetailsForm";
import {
    FORM_ENUM,
    FORM_FIELDS,
    EXTRA_FIELD,
    EXTRA_VALIDATION,
} from "../../FormData";
import styles from "../../userDetails.module.scss";
import localStyles from "./DetailsForm.module.scss";

type IFormKey = IUserDetails.IFormKey;

const DetailsForm = ({
    formfields,
    formDetails,
    setFormDetails,
    postAnalyticalEvent,
    formFieldOverrides,
    formDetailsCollection,
    formKey,
    isGstChecked,
    billingStateNonMandatory,
    isInternationalCreator,
    isTelegram,
    isGSTStateNotInapplicable,
    showGSTInfoNote,
    allowPaymentMethodToggle,
}: IDetailsForm.IProps) => {
    const dispatch = useDispatch();

    const host: IHost.IStore = useSelector(SELECTORS.host);
    const hostData: IHost.IHostData = host.dataHost.profile_data!;
    const isEmailMandatory = !hostData.is_email_non_mandatory_for_booking;
    const customerAddress: IEvent.IStore["paymentInfo"]["customerAddress"] =
        useSelector(
            (state: IStore.IState) => state.event.paymentInfo.customerAddress
        );

    // TODO: Not sure if this updateAddress is still used.
    const updateAddress = (addressProp: string) => {
        dispatch(
            createAction(ACTION_TYPES.EVENT.UPDATE_BOOKING_DATA, {
                customerAddress: addressProp,
            })
        );
    };

    // Update the value and error on input change
    const handleFormChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setFormDetails({
            key: e?.target?.name,
            value: e?.target?.value,
            // showError: true,
            error:
                FORM_ENUM?.[e.target.name]?.validate?.(e?.target?.value, {
                    isInIndia: isIndia(
                        formDetails[FORM_FIELDS.country]?.value || INDIA
                    ),
                    billingStateNonMandatory,
                    isGstChecked,
                    isInternationalCreator,
                    isTelegram,
                    formDetails,
                    isEmailMandatory,
                    disableCheckEmailTopLevelDomain:
                        hostData?.disable_booking_email_whitelisting || false,
                }) ??
                EXTRA_VALIDATION(formDetailsCollection, formKey)?.[
                    e?.target?.name
                ]?.(e?.target?.value, {
                    isGstChecked,
                }) ??
                null,
        });

        // Incase 'isGSTStateNotInapplicable' is true, only then
        // 'Billing State' field is shown in the booking form, also
        // we have to autofill this state value based upon the state code
        // from the GSTIN.
        if (
            isGSTStateNotInapplicable &&
            e?.target?.name === EXTRA_FIELD.gst_info_gst_number
        ) {
            const gstin = e?.target?.value;

            // We only check this is length is more than two,
            // as state code is atleast two letters
            if (gstin.length > 2) {
                // Taking out the state code from the GSTIN
                const stateCode = gstin.substring(0, 2);

                // See if we have a state name for this code
                const stateBasedOnGSTIN =
                    indian_state_gstin_to_state?.[stateCode];

                // If so, we auto fill the state field, and make it uneditable
                if (stateBasedOnGSTIN) {
                    setFormDetails({
                        key: EXTRA_FIELD.billing_state,
                        value: stateBasedOnGSTIN,
                        editingDisabled: true,
                        error: null,
                        showError: true,
                    });
                } else {
                    // Else, we show the user an error saying that the GSTIN number is
                    // invalid as there is no valid state for that
                    setFormDetails({
                        key: EXTRA_FIELD.gst_info_gst_number,
                        value: gstin,
                        error: "Please enter a valid GST Number",
                    });
                }
            }
        }

        // if phone or email is not mandatory then we need to reset error on other field if any one filled
        const { name } = e.target;
        if (
            !isEmailMandatory &&
            (name === FORM_FIELDS.phone || name === FORM_FIELDS.email)
        ) {
            const alternateFieldName =
                name === FORM_FIELDS.phone
                    ? FORM_FIELDS.email
                    : FORM_FIELDS.phone;
            handleErrors({
                name: alternateFieldName,
                value: formDetails[alternateFieldName].value,
            });
        }

        if (
            formfields.includes(EXTRA_FIELD.address) &&
            e?.target?.name === EXTRA_FIELD.address &&
            customerAddress !== e?.target?.value
        ) {
            updateAddress(e?.target?.value || "");
        }
    };

    const handleDropdownSelect = (option: IDropdownSelector.Option | null) => {
        setFormDetails({
            key: option?.inputName,
            value: option?.value,
            error:
                FORM_ENUM?.[option?.inputName]?.validate?.(
                    option?.value || "",
                    {
                        isInIndia: isIndia(
                            formDetails[FORM_FIELDS.country]?.value || INDIA
                        ),
                        billingStateNonMandatory,
                        isGstChecked,
                        isInternationalCreator,
                        isTelegram,
                        formDetails,
                        isEmailMandatory,
                    }
                ) ??
                EXTRA_VALIDATION(formDetailsCollection, formKey)?.[
                    option?.inputName
                ]?.(option?.value || "", {
                    isGstChecked,
                }) ??
                null,
            showError: true,
        });
    };

    const getFieldLabel = (id: string): string =>
        formFieldOverrides?.[id as IFormKey]?.label || FORM_ENUM?.[id]?.label;

    const getIsHidden = (id: string): boolean =>
        formFieldOverrides?.[id as IUserDetails.IFormKey]?.hidden ||
        FORM_ENUM?.[id]?.hidden ||
        // allowPaymentMethodToggle, BE controlled check that tells to show payment method option
        (id === EXTRA_FIELD.payment_method && !allowPaymentMethodToggle);

    const getFieldPlaceholder = (id: string) => `Enter ${getFieldLabel(id)}`;

    const getIconComponent = (id: string) => {
        const imgSrc =
            formFieldOverrides?.[id as IUserDetails.IFormKey]?.icon ||
            FORM_ENUM?.[id]?.icon;
        return imgSrc ? (
            <img
                className={styles.callIcon}
                src={imgSrc}
                alt={getFieldLabel(id)}
            />
        ) : null;
    };

    const handleErrors = ({ name, value }: { name: string; value: any }) => {
        postAnalyticalEvent({
            event_name: FORM_ENUM[name].analyticsEvent,
            action: "Tap",
            ui_component: "Input Box",
            user_input: value,
            identifier: getFieldPlaceholder(name),
        });

        setFormDetails({
            key: name,
            showError: true,
            error:
                FORM_ENUM?.[name]?.validate?.(value, {
                    isInIndia: isIndia(
                        formDetails[FORM_FIELDS.country]?.value || INDIA
                    ),
                    billingStateNonMandatory,
                    isGstChecked,
                    isInternationalCreator,
                    isTelegram,
                    formDetails,
                    isEmailMandatory,
                    disableCheckEmailTopLevelDomain:
                        hostData?.disable_booking_email_whitelisting || false,
                }) ??
                EXTRA_VALIDATION(formDetailsCollection, formKey)?.[name]?.(
                    value,
                    {
                        isGstChecked,
                    }
                ) ??
                null,
        });
    };

    // This function has two tasks:
    // 1) Show the error on input blur
    // 2) Post analytics data for the event
    const handleFieldBlur = (
        e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        handleErrors({ name, value });
    };

    const handleCountryCodeChange = (val: IDropdownSelector.Option | null) => {
        setFormDetails([
            {
                key: FORM_FIELDS.country,
                value: val?.name ?? null,
                error: isEmpty(val?.name),
            },
            {
                key: FORM_FIELDS.countryCode,
                value: val?.dial_code ?? null,
                error: isEmpty(val?.dial_code),
            },
            {
                key: FORM_FIELDS.phone,
                value: formDetails[FORM_FIELDS.phone].value,
                error:
                    FORM_ENUM?.[FORM_FIELDS.phone]?.validate?.(
                        formDetails[FORM_FIELDS.phone].value!,
                        {
                            isInIndia: isIndia(val?.name || INDIA),
                            billingStateNonMandatory,
                            isGstChecked,
                            isInternationalCreator,
                            isTelegram,
                            formDetails,
                            isEmailMandatory,
                        }
                    ) || null,
            },
        ]);
    };

    return (
        <>
            {Object.values(FORM_ENUM)
                .filter(
                    (f: IUserDetails.IFormEnum[0]) =>
                        formfields.includes(f.id) && !getIsHidden(f.id)
                )
                .map((field: IUserDetails.IFormEnum[0]) => (
                    <div key={field.id} className={styles.formField}>
                        <div
                            className={classNames(
                                styles.formTitle,
                                "d-flex align-items-center"
                            )}
                            title="This field is mandatory"
                        >
                            {getFieldLabel(field.id)}
                            {field.mandatory({
                                isInIndia: isIndia(
                                    formDetails[FORM_FIELDS.country]?.value ||
                                        INDIA
                                ),
                                billingStateNonMandatory,
                                isGstChecked,
                                isInternationalCreator,
                                isTelegram,
                                formDetails,
                                isEmailMandatory,
                            }) ? (
                                <span className={styles.mandatory}>
                                    &nbsp;*
                                </span>
                            ) : null}
                            {getIconComponent(field.id)}
                        </div>
                        <div className={styles.fieldContainer}>
                            {field.id === FORM_FIELDS.phone ? (
                                <DropdownSelector
                                    value={getCountryCodeDropdownOptionFromKey(
                                        formDetails?.[FORM_FIELDS.country]
                                            ?.value!,
                                        "name"
                                    )}
                                    id={`userInfo-${FORM_FIELDS.country}`}
                                    onChange={handleCountryCodeChange}
                                    options={
                                        countryCodes as Array<IDropdownSelector.Option>
                                    }
                                    menuPlacement="top"
                                    className={localStyles.countryCodeDropdown}
                                    editingDisabled={checkIsNeedToPrefillFormData()}
                                />
                            ) : null}
                            {field.inputType === "textarea" ? (
                                <textarea
                                    name={field.id}
                                    id={`userInfo-${field.id}`}
                                    value={formDetails[field.id]?.value || ""}
                                    className={classNames(
                                        styles.formInput,

                                        field?.id in formDetails &&
                                            formDetails[field.id]?.showError &&
                                            formDetails[field.id]?.error
                                            ? styles.formInputError
                                            : "",

                                        formDetails[field.id]
                                            ?.editingDisabled &&
                                            localStyles.editingDisabled
                                    )}
                                    placeholder={getFieldPlaceholder(field.id)}
                                    onInput={handleFormChange}
                                    onBlur={handleFieldBlur}
                                    disabled={
                                        formDetails?.[field.id]?.editingDisabled
                                    }
                                    {...field.params}
                                />
                            ) : null}
                            {["text", "tel", "email"].includes(
                                field?.inputType || ""
                            ) ? (
                                <input
                                    type={
                                        FORM_ENUM?.[field.id]?.inputType ||
                                        "text"
                                    }
                                    id={`userInfo-${field.id}`}
                                    className={classNames(
                                        styles.formInput,
                                        field.id === FORM_FIELDS.phone &&
                                            styles.phoneField,

                                        field?.id in formDetails &&
                                            formDetails[field.id]?.showError &&
                                            formDetails[field.id]?.error
                                            ? styles.formInputError
                                            : "",

                                        formDetails[field.id]
                                            ?.editingDisabled &&
                                            localStyles.editingDisabled
                                    )}
                                    name={field.id}
                                    onInput={handleFormChange}
                                    value={formDetails[field.id]?.value || ""}
                                    placeholder={getFieldPlaceholder(field.id)}
                                    onBlur={handleFieldBlur}
                                    disabled={
                                        formDetails?.[field.id]
                                            ?.editingDisabled ||
                                        ((field.id === FORM_FIELDS.phone ||
                                            field.id === FORM_FIELDS.name ||
                                            field.id === FORM_FIELDS.email) &&
                                            checkIsNeedToPrefillFormData())
                                    }
                                    {...field.params}
                                />
                            ) : null}
                            {field?.inputType === "select" ? (
                                <DropdownSelector
                                    id={`userInfo-${field.id}`}
                                    error={
                                        !!(
                                            field?.id in formDetails &&
                                            formDetails[field.id]?.showError &&
                                            formDetails[field.id]?.error
                                        )
                                    }
                                    editingDisabled={
                                        formDetails?.[field.id]?.editingDisabled
                                    }
                                    name={field.id}
                                    onChange={handleDropdownSelect}
                                    value={
                                        formDetails[field.id]?.value
                                            ? {
                                                  label: formDetails[field.id]
                                                      .value,
                                                  value: formDetails[field.id]
                                                      .value,
                                                  inputName: field.id,
                                              }
                                            : null
                                    }
                                    options={(
                                        formFieldOverrides?.[
                                            field?.id as IFormKey
                                        ]?.data ||
                                        field?.data ||
                                        []
                                    ).map((item: IDropdownSelector.Option) => ({
                                        ...item,
                                        inputName: field.id,
                                    }))}
                                    className={styles.dropdownSelector}
                                    menuPlacement="top"
                                    autoSelect="FIOOO"
                                />
                            ) : null}
                        </div>
                        <div className={styles.fieldError}>
                            {(field.id in formDetails &&
                                formDetails[field.id]?.showError &&
                                formDetails[field.id]?.error) || <>&nbsp;</>}
                        </div>
                    </div>
                ))}
            {showGSTInfoNote && (
                <div className={localStyles.noteContainer}>
                    <InfoOutlinedIcon className={localStyles.noteIconStyle} />
                    <div className={localStyles.textStyle}>
                        {gst_details_gstin_note}
                    </div>
                </div>
            )}
        </>
    );
};

export default DetailsForm;
